import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, type ReactNode } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { bookingPolicyInput, type BookingPolicyInput } from '@barbeiropro/contracts';
import { Field } from '@/components/shared/field';
import { ErrorState, LoadingState } from '@/components/shared/states';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { api } from '@/lib/api';
import { applyServerError, zodForm } from '../catalog/lib';
import { Section } from './section';

function human(min: number) {
  if (!Number.isFinite(min) || min <= 0) return 'sem exigência';
  const d = Math.floor(min / 1440);
  const h = Math.floor((min % 1440) / 60);
  const m = min % 60;
  return [d && `${d} ${d === 1 ? 'dia' : 'dias'}`, h && `${h} h`, m && `${m} min`].filter(Boolean).join(' e ');
}

export function PolicyPage() {
  const qc = useQueryClient();
  const policy = useQuery({ queryKey: ['org', 'policy'], queryFn: () => api.get<BookingPolicyInput>('/org/booking-policy') });
  const form = useForm<BookingPolicyInput>({ resolver: zodForm<BookingPolicyInput>(bookingPolicyInput) });
  const { errors, isDirty } = form.formState;

  useEffect(() => {
    if (policy.data) form.reset(bookingPolicyInput.parse(policy.data));
  }, [policy.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: (v: BookingPolicyInput) => api.put('/org/booking-policy', bookingPolicyInput.parse(v)),
    onSuccess: () => {
      toast.success('Regras de agendamento salvas');
      qc.invalidateQueries({ queryKey: ['org'] });
      qc.invalidateQueries({ queryKey: ['agenda'] });
    },
    onError: (e) => applyServerError(form, e),
  });

  if (policy.isLoading) return <LoadingState />;
  if (policy.error) return <ErrorState error={policy.error} onRetry={() => policy.refetch()} />;

  const num = (name: keyof BookingPolicyInput) => form.register(name, { valueAsNumber: true });
  const w = form.watch();

  const toggle = (name: 'allowPublicBooking' | 'publicBookingRequiresConfirmation', title: string, text: ReactNode) => (
    <Controller
      control={form.control}
      name={name}
      render={({ field }) => (
        <label className="flex items-start justify-between gap-4 rounded-md border bg-card p-3 text-sm">
          <span>
            <span className="font-medium">{title}</span>
            <span className="mt-0.5 block text-xs text-muted-foreground">{text}</span>
          </span>
          <Switch checked={field.value ?? false} onCheckedChange={field.onChange} />
        </label>
      )}
    />
  );

  return (
    <form onSubmit={form.handleSubmit((v) => save.mutate(v))} className="grid max-w-3xl gap-8" noValidate>
      <Section title="Regras de agendamento" description="Valem para a agenda interna e para o agendamento online. Mudanças não afetam agendamentos já marcados.">
        <div className="grid gap-5 sm:grid-cols-2">
          <Field id="pol-slot" label="Intervalo da grade" error={errors.slotIntervalMinutes?.message} hint="De quanto em quanto tempo a agenda oferece horários. Com 30 min, os horários são 9h, 9h30, 10h…">
            <Controller
              control={form.control}
              name="slotIntervalMinutes"
              render={({ field }) => (
                <Select value={String(field.value ?? 15)} onValueChange={(v) => field.onChange(Number(v))}>
                  <SelectTrigger id="pol-slot" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[5, 10, 15, 20, 30, 60].map((m) => (
                      <SelectItem key={m} value={String(m)}>
                        {m} minutos
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </Field>
          <Field id="pol-buffer" label="Intervalo entre atendimentos (min)" error={errors.bufferMinutes?.message} hint="Tempo livre depois de cada atendimento, para limpar a cadeira e preparar o próximo.">
            <Input id="pol-buffer" type="number" min={0} max={120} className="tabular" {...num('bufferMinutes')} />
          </Field>
          <Field id="pol-min" label="Antecedência mínima (min)" error={errors.minAdvanceMinutes?.message} hint={`Cliente só agenda com pelo menos ${human(w.minAdvanceMinutes)} de antecedência.`}>
            <Input id="pol-min" type="number" min={0} className="tabular" {...num('minAdvanceMinutes')} />
          </Field>
          <Field id="pol-max" label="Janela máxima (dias)" error={errors.maxAdvanceDays?.message} hint="Até quantos dias à frente o cliente consegue agendar.">
            <Input id="pol-max" type="number" min={1} max={365} className="tabular" {...num('maxAdvanceDays')} />
          </Field>
          <Field id="pol-cancel" label="Prazo de cancelamento (min)" error={errors.cancellationMinNoticeMinutes?.message} hint={`O cliente cancela ou remarca sozinho até ${human(w.cancellationMinNoticeMinutes)} antes do horário. Depois disso, só pela barbearia.`}>
            <Input id="pol-cancel" type="number" min={0} className="tabular" {...num('cancellationMinNoticeMinutes')} />
          </Field>
          <Field id="pol-late" label="Tolerância de atraso (min)" error={errors.lateToleranceMinutes?.message} hint="Quanto tempo a barbearia espera o cliente depois do horário marcado antes de tratar como ausência.">
            <Input id="pol-late" type="number" min={0} max={120} className="tabular" {...num('lateToleranceMinutes')} />
          </Field>
        </div>
        <div className="grid gap-3">
          {toggle('allowPublicBooking', 'Permitir agendamento online', 'Desligado, o site mostra os serviços mas não deixa o cliente marcar horário.')}
          {toggle('publicBookingRequiresConfirmation', 'Exigir confirmação dos agendamentos do site', 'Agendamentos feitos pelo site entram como "solicitado" e só valem depois que a barbearia confirma.')}
        </div>
      </Section>
      <div>
        <Button type="submit" disabled={save.isPending || !isDirty}>
          {save.isPending ? 'Salvando…' : 'Salvar regras'}
        </Button>
      </div>
    </form>
  );
}
